import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, ExternalLink, CheckCircle, XCircle, Search, BarChart3, Zap, Sparkles } from 'lucide-react';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import ReportViewerModal from '../components/ReportViewerModal';
import PdfViewerModal from '../components/PdfViewerModal';
import MvrPdfViewerModal from '../components/MvrPdfViewerModal';
import { useAuth } from '../context/AuthContext';
import { useTour } from '../context/TourContext';
import { getSearchHistory, getDashboardStats, SearchHistoryEntry, DashboardStats } from '../services/searchHistoryService';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [recentActivity, setRecentActivity] = useState<SearchHistoryEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<{ logId: string; vin: string } | null>(null);
  const [mvrPdfData, setMvrPdfData] = useState<{ logId: string; vin: string } | null>(null);
  const { startTour } = useTour();

  // Body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  // Function to fetch dashboard data (used on mount and when refreshing)
  const fetchDashboardData = useCallback(async (showLoading = true) => {
    if (!user) return;

    try {
      if (showLoading) {
        setActivityLoading(true);
      }

      // Today's Activity only — vehicle data is purged nightly per retention policy.
      // Anything older than today either has its body scrubbed (post-purge) or is hidden
      // by date filter (pre-purge), so today is the only window where modal replay works.
      const today = new Date().toISOString().split('T')[0];

      const [searchResponse, stats] = await Promise.all([
        getSearchHistory(1, 50, undefined, undefined, today, today),
        getDashboardStats()
      ]);

      setRecentActivity(searchResponse.searches);

      // Set dashboard stats from API
      setDashboardStats(stats);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      if (showLoading) {
        setActivityLoading(false);
      }
    }
  }, [user]);

  // Fetch recent activity and dashboard stats on mount
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin" />
      </div>
    );
  }

  // Don't render if not authenticated
  if (!user) {
    return null;
  }

  // Helper function to format date/time
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate percentage change for searches
  const getSearchPercentageChange = (): { value: number; isPositive: boolean } => {
    const thisMonth = dashboardStats?.searchesThisMonth ?? 0;
    const lastMonth = dashboardStats?.searchesLastMonth ?? 0;
    if (lastMonth === 0) {
      return { value: thisMonth > 0 ? 100 : 0, isPositive: true };
    }
    const change = ((thisMonth - lastMonth) / lastMonth) * 100;
    return { value: Math.abs(Math.round(change)), isPositive: change >= 0 };
  };

  // Calculate percentage change for PDF reports
  const getPdfPercentageChange = (): { value: number; isPositive: boolean } => {
    const thisMonth = dashboardStats?.pdfReportsThisMonth ?? 0;
    const lastMonth = dashboardStats?.pdfReportsLastMonth ?? 0;
    if (lastMonth === 0) {
      return { value: thisMonth > 0 ? 100 : 0, isPositive: true };
    }
    const change = ((thisMonth - lastMonth) / lastMonth) * 100;
    return { value: Math.abs(Math.round(change)), isPositive: change >= 0 };
  };

  const searchPercentageChange = getSearchPercentageChange();
  const pdfPercentageChange = getPdfPercentageChange();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Subtle Brand Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Subtle red radial gradients for depth */}
        <div className="absolute top-0 right-1/4 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.04)_0%,transparent_70%)] rounded-full"></div>
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.03)_0%,transparent_70%)] rounded-full"></div>
        <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.02)_0%,transparent_60%)] rounded-full"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Dashboard Content */}
      <div className="relative z-10 flex-1 px-4 md:px-8 py-8 pt-28 md:pt-32 lg:ml-[220px] transition-all duration-300">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="mb-8 flex items-start justify-between gap-4" data-tour="welcome">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-2">
                Welcome back, <span className="text-[#c41e3a]">{user.firstName}</span>
              </h1>
              <p className="text-[#64748b]">Your vehicle history command center</p>
            </div>
            <button
              onClick={startTour}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm text-[#475569] hover:border-[#c41e3a]/50 transition-all shrink-0"
            >
              <Sparkles className="w-4 h-4 text-[#c41e3a]" /> Take a tour
            </button>
          </div>

          {/* Key Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6" data-tour="metrics">
            {/* Metric 1: Searches This Month */}
            <div className="glass-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#c41e3a]/10 to-[#c41e3a]/5 flex items-center justify-center">
                  <Search className="w-6 h-6 text-[#c41e3a]" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-[#64748b] uppercase tracking-wider font-semibold mb-2">Searches This Month</div>
                  <div className="text-3xl font-bold text-[#0f172a] mb-1">
                    {activityLoading ? (
                      <Loader2 className="w-6 h-6 animate-spin text-[#64748b]" />
                    ) : (
                      dashboardStats?.searchesThisMonth ?? 0
                    )}
                  </div>
                  {!activityLoading && (
                    <div className={`text-sm font-semibold ${searchPercentageChange.isPositive ? 'text-[#16a34a]' : 'text-[#dc2626]'}`}>
                      {searchPercentageChange.isPositive ? '↑' : '↓'} {searchPercentageChange.value}% vs last month
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Metric 2: Reports Generated (PDF Downloads) */}
            <div className="glass-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#c41e3a]/10 to-[#c41e3a]/5 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-[#c41e3a]" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-[#64748b] uppercase tracking-wider font-semibold mb-2">Reports Generated</div>
                  <div className="text-3xl font-bold text-[#0f172a] mb-1">
                    {activityLoading ? (
                      <Loader2 className="w-6 h-6 animate-spin text-[#64748b]" />
                    ) : (
                      dashboardStats?.pdfReportsThisMonth ?? 0
                    )}
                  </div>
                  {!activityLoading && (
                    <div className={`text-sm font-semibold ${pdfPercentageChange.isPositive ? 'text-[#16a34a]' : 'text-[#dc2626]'}`}>
                      {pdfPercentageChange.isPositive ? '↑' : '↓'} {pdfPercentageChange.value}% vs last month
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Metric 3: Average Response */}
            <div className="glass-card p-6 md:col-span-2 lg:col-span-1">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#c41e3a]/10 to-[#c41e3a]/5 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-[#c41e3a]" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-[#64748b] uppercase tracking-wider font-semibold mb-2">Average Response</div>
                  <div className="text-3xl font-bold text-[#0f172a] mb-1">&lt;1s</div>
                  <div className="text-sm text-[#16a34a] font-semibold">100% Accuracy</div>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 3: Recent Activity (Full Width) */}
          <div className="glass-card-strong p-6 rounded-xl" data-tour="activity">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#0f172a]">Today's Activity</h2>
                <p className="text-sm text-[#64748b] mt-1">Searches and reports run today. Historical counts live on the Reports page.</p>
              </div>
              <button
                onClick={() => navigate('/record-inquiry-log')}
                className="text-sm text-[#c41e3a] hover:text-[#a01830] font-semibold flex items-center gap-1 transition-colors no-underline"
              >
                Reports <ExternalLink className="w-4 h-4" />
              </button>
            </div>

            {/* Activity Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-black/5">
                    <th className="text-left py-3 px-2 text-xs font-semibold text-[#64748b] uppercase tracking-wider">VIN</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold text-[#64748b] uppercase tracking-wider">Report Type</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold text-[#64748b] uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold text-[#64748b] uppercase tracking-wider">Date/Time</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold text-[#64748b] uppercase tracking-wider">Report</th>
                  </tr>
                </thead>
                <tbody>
                  {activityLoading ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-[#64748b]">
                        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#c41e3a]" />
                        <p>Loading recent activity...</p>
                      </td>
                    </tr>
                  ) : recentActivity.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-[#64748b]">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No activity today. Run your first report using Quick VIN Lookup above.</p>
                      </td>
                    </tr>
                  ) : (
                    recentActivity.map((activity) => (
                      <tr key={activity.id} className="border-b border-black/5 hover:bg-black/[0.02] transition-colors">
                        <td className="py-4 px-2">
                          <span className="font-mono text-sm text-[#0f172a]">{activity.vin}</span>
                        </td>
                        <td className="py-4 px-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            activity.requestType === 'NMVTIS'
                              ? 'badge-info'
                              : activity.requestType === 'NVS'
                              ? 'bg-rose-500/20 text-rose-600'
                              : activity.requestType === 'MVR'
                              ? 'bg-amber-500/20 text-amber-600'
                              : 'badge-purple'
                          }`}>
                            {activity.requestType === 'NVS'
                              ? 'Theft/Lien'
                              : activity.requestType === 'MVR' && activity.state
                              ? `MVR · ${activity.state}`
                              : activity.requestType}
                          </span>
                        </td>
                        <td className="py-4 px-2">
                          {activity.status === 'Success' ? (
                            <span className="inline-flex items-center gap-1.5 text-[#16a34a] text-sm font-medium">
                              <CheckCircle className="w-4 h-4" />
                              Success
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-[#dc2626] text-sm font-medium">
                              <XCircle className="w-4 h-4" />
                              Failed
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-2">
                          <span className="text-sm text-[#475569]">{formatDateTime(activity.searchedAt)}</span>
                        </td>
                        <td className="py-4 px-2">
                          {activity.status === 'Success' ? (
                            <button
                              onClick={() => {
                                if (activity.requestType === 'NMVTIS') {
                                  setPdfData({ logId: activity.id, vin: activity.vin });
                                } else if (activity.requestType === 'MVR') {
                                  setMvrPdfData({ logId: activity.id, vin: activity.vin });
                                } else {
                                  setSelectedLogId(activity.id);
                                }
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-[#fde2e8] text-[#c41e3a] text-xs font-semibold hover:bg-[#fbd0d9] transition-colors"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              View
                            </button>
                          ) : (
                            <span className="text-[#94a3b8]">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      <Footer />

      {/* Report Viewer Modal - for non-NMVTIS records */}
      {selectedLogId && (
        <ReportViewerModal
          isOpen={!!selectedLogId}
          onClose={() => {
            setSelectedLogId(null);
            // Refresh stats in case a PDF was downloaded
            fetchDashboardData(false);
          }}
          logId={selectedLogId}
        />
      )}

      {/* PDF Viewer Modal - for successful NMVTIS records */}
      {pdfData && (
        <PdfViewerModal
          isOpen={!!pdfData}
          onClose={() => {
            setPdfData(null);
            fetchDashboardData(false);
          }}
          logId={pdfData.logId}
          vin={pdfData.vin}
        />
      )}

      {/* MVR PDF Viewer Modal - for successful MVR records */}
      {mvrPdfData && (
        <MvrPdfViewerModal
          isOpen={!!mvrPdfData}
          onClose={() => {
            setMvrPdfData(null);
            fetchDashboardData(false);
          }}
          logId={mvrPdfData.logId}
          vin={mvrPdfData.vin}
        />
      )}

    </div>
  );
};

export default Dashboard;
